import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { supabase } from '../lib/supabase';
import PublicNav from '../components/PublicNav';

function useIsMobile(breakpoint = 768) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpoint);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < breakpoint);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, [breakpoint]);
  return isMobile;
}

const BLUE = '#2563eb';
const BLUE_DARK = '#1d4ed8';
const BLUE_DARKER = '#1e40af';
const BLUE_LIGHT = '#f0f9ff';
const BLUE_LIGHT_2 = '#e0f2fe';
const WHITE = '#ffffff';
const OFF_WHITE = '#f8fafc';
const TEXT = '#0a0a0a';
const TEXT_MUTED = '#555555';
const BORDER = '#e2e8f0';
const GREEN = '#16a34a';
const ORANGE = '#ea580c';
const MUTED_GRAY = '#94a3b8';
const FONT = "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif";

function FadeIn({ children, delay = 0, isMobile, style, axis }) {
  const offset = isMobile ? 12 : 20;
  const duration = isMobile ? 0.4 : 0.6;
  const initial = axis === 'x' ? { opacity: 0, x: -offset * 1.5 } : { opacity: 0, y: offset };
  const whileInView = axis === 'x' ? { opacity: 1, x: 0 } : { opacity: 1, y: 0 };
  return (
    <motion.div
      initial={initial}
      whileInView={whileInView}
      viewport={{ once: true, amount: 0.3 }}
      transition={{ duration, ease: 'easeOut', delay }}
      style={style}
    >
      {children}
    </motion.div>
  );
}

function HoverBtn({ to, children, variant = 'solid', big, full, style }) {
  const [hover, setHover] = useState(false);
  const base = {
    fontFamily: FONT,
    fontWeight: 700,
    textDecoration: 'none',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 10,
    cursor: 'pointer',
    padding: big ? '16px 36px' : '9px 20px',
    fontSize: big ? 16 : 13,
    width: full ? '100%' : 'auto',
    transition: 'transform 0.15s ease, box-shadow 0.15s ease, background 0.15s ease',
    transform: hover ? 'translateY(-2px)' : 'translateY(0)',
    boxSizing: 'border-box',
  };
  const variants = {
    solid: {
      background: hover ? BLUE_DARK : BLUE,
      color: WHITE,
      border: '1px solid ' + BLUE,
      boxShadow: hover ? '0 10px 24px rgba(37,99,235,0.35)' : '0 4px 10px rgba(37,99,235,0.2)',
    },
    onBlue: {
      background: hover ? '#f8fafc' : WHITE,
      color: BLUE_DARKER,
      border: '1px solid ' + WHITE,
      boxShadow: hover ? '0 10px 24px rgba(0,0,0,0.2)' : '0 4px 10px rgba(0,0,0,0.12)',
    },
  };
  return (
    <Link to={to} style={{ ...base, ...variants[variant], ...style }} onMouseEnter={() => setHover(true)} onMouseLeave={() => setHover(false)}>
      {children}
    </Link>
  );
}

function Eyebrow({ children }) {
  return (
    <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: BLUE, marginBottom: 14 }}>
      {children}
    </div>
  );
}

function WaveDivider({ fill }) {
  return (
    <div style={{ lineHeight: 0, marginTop: -1 }}>
      <svg viewBox="0 0 1440 80" width="100%" height="60" preserveAspectRatio="none" style={{ display: 'block' }}>
        <path d="M0,32 C240,80 480,0 720,24 C960,48 1200,72 1440,24 L1440,80 L0,80 Z" fill={fill} />
      </svg>
    </div>
  );
}

const TIMELINE = [
  { when: '6 months out', title: 'MCAT prep begins', body: 'Most successful applicants put in 300-400 hours of focused study before sitting for the exam.' },
  { when: '3-4 months out', title: 'Sit for the MCAT', body: 'Aim to test in the spring of your application year so scores are back in time for your primary.' },
  { when: 'June 1', title: 'Primary application opens', body: 'AMCAS opens for submission. Earlier is almost always better -- schools review on a rolling basis.' },
  { when: 'Jul - Aug', title: 'Secondary essays due', body: 'Schools send secondaries almost immediately after your primary is verified. Turn each around within two weeks.' },
  { when: 'Oct - Jan', title: 'Interview season', body: 'Invitations roll in through the fall and winter. Most interviews happen October through January.' },
  { when: 'Mar - May', title: 'Decisions arrive', body: 'Acceptances, waitlists, and rejections land through the spring. May 1st is the deadline to commit to one school.' },
];

const STAT_BULLETS = [
  'MCAT opens doors to schools within reach',
  'GPA tells your work ethic story',
  "Together they're maybe 50% of your application",
  'The other 50%? Your clinical hours, experiences, essays, interviews',
];

const FACTORS = [
  { title: 'Clinical experience', icon: 'M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z', body: 'Shadowing and volunteering prove you know what you’re signing up for. Schools want real hours, not a single afternoon.' },
  { title: 'Your story', icon: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z', body: 'Essays and interviews are where you stop being a spreadsheet and become a person committees can picture as a colleague.' },
  { title: 'Research (maybe)', icon: 'M9.75 3.104v5.714a2.25 2.25 0 01-.659 1.591L5 14.5M9.75 3.104c-.251.023-.501.05-.75.082m.75-.082a24.301 24.301 0 014.5 0m0 0v5.714c0 .597.237 1.17.659 1.591L19.8 15.3M14.25 3.104c.251.023.501.05.75.082M19.8 15.3l-1.57.393A9.065 9.065 0 0112 15a9.065 9.065 0 00-6.23-.693L5 14.5m14.8.8l1.402 1.402c1.232 1.232.65 3.318-1.067 3.611A48.309 48.309 0 0112 21c-2.773 0-5.491-.235-8.135-.687-1.718-.293-2.3-2.379-1.067-3.61L5 14.5', body: 'Research-heavy schools want it. Most programs don’t require it. Clinical experience matters more almost everywhere.' },
  { title: 'Fit', icon: 'M15 10.5a3 3 0 11-6 0 3 3 0 016 0z M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z', body: 'Geography, mission alignment, MD vs DO -- schools aren’t just picking the best applicants, they’re picking the right ones for them.' },
];

const RTS = [
  { tier: 'REACH', pct: '20-30% acceptance rate', examples: 'Harvard, Stanford, UCSF', color: ORANGE },
  { tier: 'TARGET', pct: '40-60% acceptance rate', examples: 'BU, Case Western, Georgetown', color: BLUE },
  { tier: 'SAFETY', pct: '80%+ acceptance rate', examples: 'Where your stats sit above the median', color: GREEN },
];

const MYTHS = [
  { myth: 'You need a 520+ MCAT to get in anywhere', reality: 'Many schools accept 500-510. It matters less than you think.' },
  { myth: 'You need research to get accepted', reality: 'Clinical experience matters way more. Research is a bonus.' },
  { myth: 'GPA has to be 3.9+', reality: '3.7+ is competitive at most schools. Upward trend matters more.' },
];

const INTERVIEW_BULLETS = [
  'They want to know your story, not perfect answers',
  'Be authentic. Schools reject robots.',
  "Prepare, but don't memorize",
];

export default function Guide() {
  const isMobile = useIsMobile();
  const px = isMobile ? 20 : 60;
  const sectionPad = isMobile ? '56px 0' : '100px 0';
  const [session, setSession] = useState(null);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => setSession(session));
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => setSession(session));
    return () => subscription.unsubscribe();
  }, []);

  return (
    <div style={{ background: WHITE, minHeight: '100vh', fontFamily: FONT }}>
      <PublicNav isMobile={isMobile} active="guide" />

      {/* Hero */}
      <div style={{ background: `linear-gradient(180deg, ${BLUE_LIGHT} 0%, ${BLUE_LIGHT_2} 100%)`, padding: isMobile ? `56px ${px}px 60px` : `100px ${px}px 90px`, textAlign: 'center' }}>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: 'easeOut' }}
          style={{ maxWidth: 760, margin: '0 auto' }}
        >
          <div style={{ fontSize: 12, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '.1em', color: BLUE, background: WHITE, border: '1px solid ' + BLUE_LIGHT_2, borderRadius: 20, padding: '5px 14px', display: 'inline-block', marginBottom: 24 }}>
            Free guide
          </div>
          <div style={{ fontSize: isMobile ? 32 : 48, fontWeight: 800, color: TEXT, letterSpacing: '-1.5px', lineHeight: 1.1, marginBottom: 20 }}>
            Everything you need to know about medical school.
          </div>
          <div style={{ fontSize: isMobile ? 15 : 18, color: TEXT_MUTED, lineHeight: 1.6, maxWidth: 560, margin: '0 auto' }}>
            From MCAT to Match Day. Real talk, real data.
          </div>
        </motion.div>
      </div>
      <WaveDivider fill={WHITE} />

      {/* Section 1: Timeline */}
      <div style={{ padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile}>
            <Eyebrow>The real timeline</Eyebrow>
            <div style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              18 months. Here&rsquo;s what to expect.
            </div>
            <div style={{ fontSize: 16, color: TEXT_MUTED, lineHeight: 1.7, marginBottom: 44 }}>
              The application process isn&rsquo;t a single event &mdash; it&rsquo;s a year-and-a-half marathon that starts long before you hit submit. Here&rsquo;s the real timeline, step by step.
            </div>
          </FadeIn>
          <div style={{ position: 'relative' }}>
            <motion.div
              initial={{ height: 0 }}
              whileInView={{ height: '100%' }}
              viewport={{ once: true, amount: 0.1 }}
              transition={{ duration: 1.2, ease: 'easeInOut' }}
              style={{ position: 'absolute', left: 3, top: 6, width: 2, background: BLUE_LIGHT_2 }}
            />
            {TIMELINE.map(function (t, i) {
              return (
                <FadeIn key={t.title} isMobile={isMobile} delay={i * 0.08} style={{ position: 'relative', paddingLeft: 28, marginBottom: i === TIMELINE.length - 1 ? 0 : 32 }}>
                  <div style={{ position: 'absolute', left: 0, top: 6, width: 8, height: 8, borderRadius: '50%', background: BLUE }} />
                  <div style={{ fontSize: 12, fontWeight: 800, color: BLUE, textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 4 }}>{t.when}</div>
                  <div style={{ fontSize: 17, fontWeight: 700, color: TEXT, marginBottom: 6 }}>{t.title}</div>
                  <div style={{ fontSize: 15, color: TEXT_MUTED, lineHeight: 1.6, maxWidth: 560 }}>{t.body}</div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 2: Stats aren't everything */}
      <div style={{ background: BLUE_LIGHT, padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile}>
            <Eyebrow>Your scores matter. But not like you think.</Eyebrow>
            <div style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              MCAT and GPA are doors. Not the whole house.
            </div>
            <div style={{ fontSize: 16, color: TEXT_MUTED, lineHeight: 1.7, marginBottom: 32 }}>
              Every pre-med obsesses over their MCAT and GPA. Here&rsquo;s the truth: they matter, but not the way you think.
            </div>
          </FadeIn>
          {STAT_BULLETS.map(function (b, i) {
            return (
              <FadeIn key={b} isMobile={isMobile} delay={i * 0.12} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: WHITE, border: '2px solid ' + BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                </div>
                <div style={{ fontSize: 16, color: TEXT, fontWeight: 600, lineHeight: 1.5 }}>{b}</div>
              </FadeIn>
            );
          })}
        </div>
      </div>

      {/* Section 3: What schools actually care about */}
      <div style={{ padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 900, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile} style={{ maxWidth: 700, marginBottom: 44 }}>
            <Eyebrow>What schools actually care about</Eyebrow>
            <div style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              Beyond the spreadsheet.
            </div>
          </FadeIn>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 16 }}>
            {FACTORS.map(function (f, i) {
              return (
                <FadeIn key={f.title} isMobile={isMobile} delay={i * 0.1}>
                  <div style={{ background: WHITE, border: '1px solid ' + BORDER, borderRadius: 14, padding: 24, height: '100%', boxSizing: 'border-box', boxShadow: '0 10px 30px rgba(15,23,42,0.05)' }}>
                    <div style={{ width: 40, height: 40, borderRadius: 10, background: BLUE_LIGHT, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 14 }}>
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="2"><path strokeLinecap="round" strokeLinejoin="round" d={f.icon} /></svg>
                    </div>
                    <div style={{ fontSize: 17, fontWeight: 700, color: TEXT, marginBottom: 8 }}>{f.title}</div>
                    <div style={{ fontSize: 14, color: TEXT_MUTED, lineHeight: 1.6 }}>{f.body}</div>
                  </div>
                </FadeIn>
              );
            })}
          </div>
        </div>
      </div>

      {/* Section 4: R-T-S strategy */}
      <div style={{ background: BLUE_LIGHT, padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile}>
            <Eyebrow>The application strategy</Eyebrow>
            <div style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              School selection is everything.
            </div>
            <div style={{ fontSize: 16, color: TEXT_MUTED, lineHeight: 1.7, marginBottom: 32 }}>
              A balanced list is the single highest-leverage decision in your entire application. Here&rsquo;s how to build one.
            </div>
          </FadeIn>
          {RTS.map(function (r, i) {
            return (
              <FadeIn key={r.tier} isMobile={isMobile} axis="x" delay={i * 0.12} style={{ marginBottom: 14 }}>
                <div style={{ background: WHITE, border: '1px solid ' + BORDER, borderLeft: '4px solid ' + r.color, borderRadius: 10, padding: '18px 20px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 800, color: r.color, letterSpacing: '.05em', marginBottom: 6 }}>{r.tier}</div>
                    <div style={{ fontSize: 15, color: TEXT, fontWeight: 600 }}>{r.examples}</div>
                  </div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: TEXT_MUTED, whiteSpace: 'nowrap' }}>{r.pct}</div>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>

      {/* Section 5: Myths debunked */}
      <div style={{ padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile}>
            <Eyebrow>Common myths, debunked</Eyebrow>
            <div style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 32 }}>
              What you&rsquo;ve heard that&rsquo;s wrong.
            </div>
          </FadeIn>
          {MYTHS.map(function (m, i) {
            return (
              <FadeIn key={m.myth} isMobile={isMobile} delay={i * 0.12} style={{ marginBottom: 16 }}>
                <div style={{ background: OFF_WHITE, border: '1px solid ' + BORDER, borderRadius: 12, padding: 20 }}>
                  <div style={{ fontSize: 14, color: MUTED_GRAY, textDecoration: 'line-through', marginBottom: 10 }}>&ldquo;{m.myth}&rdquo;</div>
                  <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
                    <div style={{ fontSize: 12, fontWeight: 800, color: BLUE, textTransform: 'uppercase', letterSpacing: '.05em', flexShrink: 0, marginTop: 2 }}>Reality</div>
                    <div style={{ fontSize: 15, color: TEXT, fontWeight: 600, lineHeight: 1.5 }}>{m.reality}</div>
                  </div>
                </div>
              </FadeIn>
            );
          })}
        </div>
      </div>

      {/* Section 6: Secondaries */}
      <div style={{ background: OFF_WHITE, padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile}>
            <Eyebrow>Secondary essays are your real test</Eyebrow>
            <div style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              Primaries get you in the door. Secondaries win interviews.
            </div>
            <div style={{ fontSize: 16, color: TEXT_MUTED, lineHeight: 1.7, marginBottom: 20 }}>
              Your primary application is a formality compared to what comes next. Secondaries are where schools actually decide who gets an interview.
            </div>
          </FadeIn>
          <FadeIn isMobile={isMobile} delay={0.1} style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 20 }}>
            {['Why you, specifically', 'Mission alignment', 'Clinical depth'].map(function (tag) {
              return (
                <div key={tag} style={{ background: WHITE, border: '1px solid ' + BLUE_LIGHT_2, color: BLUE, fontSize: 13, fontWeight: 700, borderRadius: 20, padding: '7px 16px' }}>{tag}</div>
              );
            })}
          </FadeIn>
          <FadeIn isMobile={isMobile} delay={0.18}>
            <div style={{ fontSize: 16, color: TEXT_MUTED, lineHeight: 1.7 }}>
              Generic secondaries are the single most common reason strong applicants get quietly rejected. Every answer should read like it could only have been written for that school.
            </div>
          </FadeIn>
        </div>
      </div>

      {/* Section 7: The interview */}
      <div style={{ padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile} axis="x">
            <Eyebrow>The interview</Eyebrow>
            <div style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              This is where you become real.
            </div>
            <div style={{ fontSize: 16, color: TEXT_MUTED, lineHeight: 1.7, marginBottom: 32 }}>
              By the time you&rsquo;re sitting across from an interviewer, your stats already got you there. This conversation is about everything else.
            </div>
          </FadeIn>
          {INTERVIEW_BULLETS.map(function (b, i) {
            return (
              <FadeIn key={b} isMobile={isMobile} axis="x" delay={i * 0.1} style={{ display: 'flex', alignItems: 'flex-start', gap: 12, marginBottom: 16 }}>
                <div style={{ width: 22, height: 22, borderRadius: '50%', background: BLUE_LIGHT, border: '2px solid ' + BLUE, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 2 }}>
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={BLUE} strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 13l4 4L19 7" /></svg>
                </div>
                <div style={{ fontSize: 16, color: TEXT, fontWeight: 600, lineHeight: 1.5 }}>{b}</div>
              </FadeIn>
            );
          })}
        </div>
      </div>

      {/* Section 8: After you get in */}
      <div style={{ background: OFF_WHITE, padding: `${sectionPad} ${px}px` }}>
        <div style={{ maxWidth: 700, margin: '0 auto' }}>
          <FadeIn isMobile={isMobile}>
            <Eyebrow>After you get in</Eyebrow>
            <div style={{ fontSize: isMobile ? 28 : 40, fontWeight: 800, color: TEXT, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 18 }}>
              Match Day is the beginning. Not the end.
            </div>
            <div style={{ fontSize: 16, color: TEXT_MUTED, lineHeight: 1.7 }}>
              Getting your acceptance letter feels like crossing the finish line. It isn&rsquo;t. Four years of training stand between you and Match Day &mdash; the day you find out where you&rsquo;ll complete residency. That&rsquo;s when your medical career actually begins.
            </div>
          </FadeIn>
        </div>
      </div>

      <WaveDivider fill={BLUE_DARKER} />

      {/* CTA */}
      <div style={{ background: `linear-gradient(135deg, ${BLUE} 0%, ${BLUE_DARKER} 100%)`, padding: isMobile ? `52px ${px}px 60px` : `90px ${px}px`, textAlign: 'center' }}>
        <FadeIn isMobile={isMobile} style={{ maxWidth: 640, margin: '0 auto' }}>
          <div style={{ fontSize: isMobile ? 26 : 40, fontWeight: 800, color: WHITE, letterSpacing: '-1px', lineHeight: 1.15, marginBottom: 16 }}>
            Ready to find your schools?
          </div>
          <div style={{ fontSize: isMobile ? 14 : 16, color: BLUE_LIGHT_2, lineHeight: 1.6, marginBottom: 32 }}>
            Enter your stats &mdash; PreVisr matches you to the right schools in seconds.
          </div>
          <HoverBtn to={session ? '/matcher' : '/signup'} variant="onBlue" big full={isMobile}>Build your school list</HoverBtn>
        </FadeIn>
      </div>

      {/* Footer */}
      <div style={{ borderTop: '1px solid ' + BORDER, padding: isMobile ? `24px ${px}px` : '28px 60px', display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', justifyContent: 'space-between', gap: isMobile ? 14 : 0 }}>
        <div style={{ fontSize: 13, color: TEXT_MUTED }}>&copy; {new Date().getFullYear()} PreVisr &mdash; Medical school matching for pre-meds</div>
        <div style={{ display: 'flex', gap: 24 }}>
          <Link to="/" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none' }}>Back to Landing</Link>
          <Link to="/" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none' }}>Pricing</Link>
          <Link to="/stats" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none' }}>Stats</Link>
          <Link to="/privacy" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none' }}>Privacy</Link>
          <Link to="/terms" style={{ fontSize: 12, color: TEXT_MUTED, textDecoration: 'none' }}>Terms</Link>
        </div>
      </div>

    </div>
  );
}
